/**
 * A folder window's toolbar, per desktop style.
 *
 *   Windows — the Explorer command bar: Back, Forward, Up, the address (each
 *             part of the path is a place to go), then Sort and View menus.
 *   Mac     — Finder's unified toolbar: back / forward, the folder's name,
 *             the view buttons (Icons, List, Columns, Gallery) and a
 *             "Sort By" menu.
 *
 * Both edit the same per-folder view; the status bar (item counts, and the
 * Mac icon-size slider) is `WindowStatusBar`.
 */
import {
  ArrowDownUp,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Check,
  ChevronLeft,
  ChevronRight,
  Columns3,
  GalleryHorizontalEnd,
  LayoutGrid,
  List,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SORT_KEYS, type FolderView, type SortKey, type ViewMode } from '../../lib/desktop-layout';
import {
  ICON_MODES,
  SORT_LABELS,
  STYLE_MODES,
  firstDirection,
  isIconMode,
  menuModeFor,
  type DesktopStyle,
} from '../../lib/folder-view';

export interface PathPart {
  /** A container: `root`, a folder id, or `trash`. */
  container: string;
  name: string;
}

export interface WindowToolbarProps {
  style: DesktopStyle;
  title: string;
  /** Where the window is, root first (for the Windows address bar). */
  path: readonly PathPart[];
  view: FolderView;
  onView: (view: FolderView) => void;
  canBack: boolean;
  canForward: boolean;
  /** Absent at the top (Documents) and in the Trash. */
  onUp?: () => void;
  onBack: () => void;
  onForward: () => void;
  onNavigate: (container: string) => void;
  /** The Trash has no Columns / Gallery and no Up. */
  trash?: boolean;
}

const MAC_VIEW_ICONS: Partial<Record<ViewMode, typeof LayoutGrid>> = {
  'icons-md': LayoutGrid,
  details: List,
  columns: Columns3,
  gallery: GalleryHorizontalEnd,
};

export function WindowToolbar(props: WindowToolbarProps) {
  return props.style === 'mac' ? <MacToolbar {...props} /> : <WindowsCommandBar {...props} />;
}

function WindowsCommandBar(props: WindowToolbarProps) {
  const { view, onView } = props;
  const navButton =
    'flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-40';
  return (
    <div className="flex items-center gap-1 text-xs">
      <button
        type="button"
        className={navButton}
        aria-label="Back"
        title="Back"
        disabled={!props.canBack}
        onClick={props.onBack}
      >
        <ArrowLeft className="h-4 w-4" />
      </button>
      <button
        type="button"
        className={navButton}
        aria-label="Forward"
        title="Forward"
        disabled={!props.canForward}
        onClick={props.onForward}
      >
        <ArrowRight className="h-4 w-4" />
      </button>
      <button
        type="button"
        className={navButton}
        aria-label="Up"
        title="Up (Alt+↑)"
        disabled={!props.onUp}
        onClick={props.onUp}
      >
        <ArrowUp className="h-4 w-4" />
      </button>
      <nav
        aria-label="Address"
        className="mx-1 flex h-7 min-w-0 flex-1 items-center overflow-hidden rounded border border-border bg-background px-2"
      >
        {props.path.map((part, i) => (
          <span key={part.container} className="flex min-w-0 items-center">
            {i > 0 && <ChevronRight className="mx-0.5 h-3 w-3 shrink-0 text-muted-foreground" />}
            <button
              type="button"
              onClick={() => props.onNavigate(part.container)}
              className={cn(
                'min-w-0 truncate rounded px-1 hover:bg-muted',
                i === props.path.length - 1 ? 'text-foreground' : 'text-muted-foreground',
              )}
            >
              {part.name}
            </button>
          </span>
        ))}
      </nav>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex h-7 items-center gap-1 rounded px-2 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <ArrowDownUp className="h-3.5 w-3.5" />
            Sort
          </button>
        </DropdownMenuTrigger>
        <SortMenuContent view={view} onView={onView} style="windows" />
      </DropdownMenu>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex h-7 items-center gap-1 rounded px-2 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <LayoutGrid className="h-3.5 w-3.5" />
            View
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          {STYLE_MODES.windows.map((option) => (
            <DropdownMenuItem
              key={option.mode}
              onSelect={() => onView({ ...view, mode: option.mode })}
              className="gap-2"
            >
              <Check
                className={cn(
                  'h-3.5 w-3.5',
                  menuModeFor(view.mode, 'windows') === option.mode ? 'opacity-100' : 'opacity-0',
                )}
              />
              {option.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function MacToolbar(props: WindowToolbarProps) {
  const { view, onView } = props;
  const current = menuModeFor(view.mode, 'mac');
  const modes = props.trash
    ? STYLE_MODES.mac.filter((m) => m.mode === 'icons-md' || m.mode === 'details')
    : STYLE_MODES.mac;
  const navButton =
    'flex h-7 w-7 items-center justify-center rounded-md text-foreground/70 hover:bg-black/5 disabled:opacity-30 dark:hover:bg-white/10';
  return (
    <div className="flex min-w-0 flex-1 items-center gap-2">
      <div className="flex shrink-0 items-center">
        <button
          type="button"
          className={navButton}
          aria-label="Back"
          title="Back"
          disabled={!props.canBack}
          onClick={props.onBack}
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          type="button"
          className={navButton}
          aria-label="Forward"
          title="Forward"
          disabled={!props.canForward}
          onClick={props.onForward}
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
      <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-foreground/85">
        {props.title}
      </span>
      <div
        role="radiogroup"
        aria-label="View"
        className="flex shrink-0 items-center rounded-md bg-black/5 p-0.5 dark:bg-white/10"
      >
        {modes.map((option) => {
          const Icon = MAC_VIEW_ICONS[option.mode] ?? LayoutGrid;
          const on = current === option.mode;
          return (
            <button
              key={option.mode}
              type="button"
              role="radio"
              aria-checked={on}
              aria-label={`View ${option.label}`}
              title={option.label}
              onClick={() =>
                onView({
                  ...view,
                  // Icons keeps the chosen size; the other views are one mode each.
                  mode:
                    option.mode === 'icons-md' && isIconMode(view.mode) ? view.mode : option.mode,
                })
              }
              className={cn(
                'flex h-6 w-7 items-center justify-center rounded',
                on ? 'bg-background text-foreground shadow-sm' : 'text-foreground/60',
              )}
            >
              <Icon className="h-3.5 w-3.5" />
            </button>
          );
        })}
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Sort By"
            title="Sort By"
            className="flex h-7 w-8 shrink-0 items-center justify-center rounded-md text-foreground/70 hover:bg-black/5 dark:hover:bg-white/10"
          >
            <ArrowDownUp className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <SortMenuContent view={view} onView={onView} style="mac" />
      </DropdownMenu>
    </div>
  );
}

function SortMenuContent({
  view,
  onView,
  style,
}: {
  view: FolderView;
  onView: (view: FolderView) => void;
  style: DesktopStyle;
}) {
  return (
    <DropdownMenuContent align="end" className="w-48">
      <DropdownMenuLabel>{style === 'mac' ? 'Sort By' : 'Sort by'}</DropdownMenuLabel>
      {SORT_KEYS.map((key: SortKey) => (
        <DropdownMenuItem
          key={key}
          onSelect={() =>
            onView({ ...view, sort: key, dir: view.sort === key ? view.dir : firstDirection(key) })
          }
          className="gap-2"
        >
          <Check className={cn('h-3.5 w-3.5', view.sort === key ? 'opacity-100' : 'opacity-0')} />
          {style === 'mac' && key === 'modified' ? 'Date Modified' : SORT_LABELS[key]}
        </DropdownMenuItem>
      ))}
      <DropdownMenuSeparator />
      {(['asc', 'desc'] as const).map((dir) => (
        <DropdownMenuItem key={dir} onSelect={() => onView({ ...view, dir })} className="gap-2">
          <Check className={cn('h-3.5 w-3.5', view.dir === dir ? 'opacity-100' : 'opacity-0')} />
          {dir === 'asc' ? 'Ascending' : 'Descending'}
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  );
}

/** Item counts, and on a Mac in Icons view the icon-size slider. */
export function WindowStatusBar({
  style,
  itemCount,
  totalCount,
  selectedCount,
  view,
  onView,
}: {
  style: DesktopStyle;
  itemCount: number;
  /** When more exist than are loaded. */
  totalCount?: number;
  selectedCount: number;
  view: FolderView;
  onView: (view: FolderView) => void;
}) {
  const count = totalCount && totalCount > itemCount ? totalCount : itemCount;
  const items = `${count} ${count === 1 ? 'item' : 'items'}`;
  const iconIndex = ICON_MODES.indexOf(view.mode);
  return (
    <>
      <span className="truncate">
        {style === 'mac' && selectedCount > 0
          ? `${selectedCount} of ${items} selected`
          : selectedCount > 0
            ? `${items}  |  ${selectedCount} selected`
            : items}
      </span>
      {style === 'mac' && iconIndex >= 0 && (
        <label className="ml-auto flex items-center gap-2">
          <span className="sr-only">Icon size</span>
          <LayoutGrid className="h-2.5 w-2.5" aria-hidden />
          <input
            type="range"
            min={0}
            max={ICON_MODES.length - 1}
            step={1}
            value={iconIndex}
            aria-label="Icon size"
            onChange={(e) => onView({ ...view, mode: ICON_MODES[Number(e.target.value)] })}
            className="h-1 w-24 accent-[#0a64d6]"
          />
          <LayoutGrid className="h-3.5 w-3.5" aria-hidden />
        </label>
      )}
    </>
  );
}
